import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import { EmailResponse, sealingService } from '@paragon/data-connections';
import { enqueueError, enqueueInfo } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { getSelectedDocument } from '../selectors/documents';
import { savePageAnnotations } from './annotations';
import { AsyncThunkActionVoid } from './thunkActions';

const PDF_AS_EMAIL_ALERT_TEXT =
  'Because this job has more than 100 PDFs, an email will be sent to you when the requested download is ready.';

export function downloadSeparatedPdfs(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Generating separated PDFs...'));

    try {
      await dispatch(savePageAnnotations());
      const response = await sealingService.jobs.downloadSeparated(getState().job.jobId);

      if (response instanceof EmailResponse) {
        dispatch(enqueueInfo(PDF_AS_EMAIL_ALERT_TEXT));
      } else {
        response.download();
      }
    } catch (err) {
      enqueueError(getUserVisibleOrDefaultExceptionMessage(err, 'Error downloading separated PDFs'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function downloadMergedPdfs(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Generating merged PDF...'));

    try {
      await dispatch(savePageAnnotations());
      const response = await sealingService.jobs.downloadMerged(getState().job.jobId);

      if (response instanceof EmailResponse) {
        dispatch(enqueueInfo(PDF_AS_EMAIL_ALERT_TEXT));
      } else {
        response.download();
      }
    } catch (err) {
      enqueueError(getUserVisibleOrDefaultExceptionMessage(err, 'Error downloading merged PDFs'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function downloadMiscellaneousFiles(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Downloading miscellaneous files...'));

    try {
      const response = await sealingService.jobs.downloadMiscellaneous(getState().job.jobId);
      response.download();
    } catch (err) {
      enqueueError(getUserVisibleOrDefaultExceptionMessage(err, 'Error downloading miscellaneous files'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function downloadComponentDesignFiles(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const selectedDocument = getSelectedDocument(getState());
    const spinnerId = dispatch(addSpinnerMessage('Downloading component design files...'));

    try {
      if (selectedDocument.componentDesignId == null) {
        await sealingService.pdfs.download(selectedDocument.id);
      } else {
        await sealingService.componentDesigns.download(selectedDocument.componentDesignId);
      }
    } catch (err) {
      enqueueError(getUserVisibleOrDefaultExceptionMessage(err, 'Error downloading component design files'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
